import React from 'react';

import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';

import { Comment } from './hooks/useTaskComments';


interface CommentItemProps {
  comment: Comment;
  isEditing: boolean;
  editCommentText: string;
  setEditCommentText: (value: string) => void;
  editPendingFiles: Array<{id: string, file: File, preview?: string}>;
  editExistingAttachments: Array<{id: number, name: string}>;
  setDeletedAttachmentIds: React.Dispatch<React.SetStateAction<number[]>>;
  setEditPendingFiles: React.Dispatch<React.SetStateAction<Array<{id: string, file: File, preview?: string}>>>;
  setEditExistingAttachments: React.Dispatch<React.SetStateAction<Array<{id: number, name: string}>>>;
  savingEdit: boolean;
  deletingCommentId: number | null;
  editingCommentId: number | null;
  getDeviceIcon: (device: string) => string;
  getAttachmentPreview: (attachment: any) => string | null;
  formatDate: (date: Date) => string;
  handleEditComment: (comment: Comment) => void;
  handleDeleteComment: (commentId: number, userName: string) => void;
  handleCancelEdit: () => void;
  handleSaveEditComment: (commentId: number) => void;
  handleEditFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAttachmentDoubleClick: (attachment: any) => void;
  handleAttachmentDownload: (attachment: any) => void;
  editFileInputRef: React.RefObject<HTMLInputElement>;
  MAX_FILES: number;
}

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  isEditing,
  editCommentText,
  setEditCommentText,
  editPendingFiles,
  editExistingAttachments,
  setDeletedAttachmentIds,
  setEditPendingFiles,
  setEditExistingAttachments,
  savingEdit,
  deletingCommentId,
  editingCommentId,
  getDeviceIcon,
  getAttachmentPreview,
  formatDate,
  handleEditComment,
  handleDeleteComment,
  handleCancelEdit,
  handleSaveEditComment,
  handleEditFileSelect,
  handleAttachmentDoubleClick,
  handleAttachmentDownload,
  editFileInputRef,
  MAX_FILES,
}) => {
  return (
    <div
      className="p-3 mb-3 surface-card shadow-2 border-round-lg"
      style={{ 
        borderLeft: isEditing ? '3px solid #3b82f6' : '3px solid var(--primary-color)',
        width: '99.8%',
        background: isEditing ? '#f8fafc' : undefined
      }}
    >
      {isEditing ? (
        
        <>
          <div className="flex justify-content-between align-items-start mb-2">
            <div className="flex align-items-center gap-2 text-600">
              <span style={{ fontWeight: 'bold', color: '#3b82f6' }}>{comment.userName}</span>
              <i 
                className={getDeviceIcon(comment.device)} 
                style={{ fontSize: '12px', color: '#afafaf' }}
              />
              <Badge value="Редактирование" severity="info" />
            </div>
          </div>

          <div className="relative">
            <InputTextarea
              value={editCommentText}
              onChange={(e) => {
                if (e.target.value.length <= 400) {
                  setEditCommentText(e.target.value);
                }
              }}
              placeholder="Введите текст комментария..."
              rows={4}
              maxLength={400}
              className="w-full"
              style={{ fontSize: '14px' }}
              disabled={savingEdit}
            />
            <Badge 
              value={`${editCommentText.length}/400`} 
              severity={editCommentText.length >= 380 ? 'warning' : undefined}
              className="absolute"
              style={{ 
                bottom: '8px', 
                right: '8px',
                zIndex: 1
              }}
            />
          </div>

          <input
            ref={editFileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleEditFileSelect}
          />

          <div className="mt-2">
            {editExistingAttachments.length > 0 && (
              <div className="flex align-items-center gap-2 flex-wrap mb-2">
                {editExistingAttachments.map((attachment) => (
                  <div 
                    key={attachment.id} 
                    className="flex align-items-center gap-2 bg-gray-100 p-2 border-round"
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="pi pi-file" style={{ fontSize: '24px', color: '#6c757d' }} />
                    <span className="text-sm" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {attachment.name}
                    </span>
                    <Button
                      type="button"
                      icon="pi pi-times"
                      className="p-button-text p-button-rounded p-button-sm p-button-danger"
                      onClick={() => {
                        setDeletedAttachmentIds(prev => [...prev, attachment.id]);
                        setEditExistingAttachments(prev => prev.filter(a => a.id !== attachment.id));
                      }}
                      style={{ width: '24px', height: '24px' }}
                      disabled={savingEdit}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Новые файлы */}
            {editPendingFiles.length > 0 && (
              <div className="flex align-items-center gap-2 flex-wrap mb-2">
                {editPendingFiles.map((pendingFile) => (
                  <div 
                    key={pendingFile.id} 
                    className="flex align-items-center gap-2 bg-gray-100 p-2 border-round"
                    style={{ borderRadius: '8px' }}
                  >
                    {pendingFile.preview ? (
                      <img 
                        src={pendingFile.preview} 
                        alt="preview" 
                        style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }} 
                      />
                    ) : (
                      <i className="pi pi-file" style={{ fontSize: '24px', color: '#6c757d' }} />
                    )}
                    <span className="text-sm" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {pendingFile.file.name}
                    </span>
                    <span className="text-xs text-500">{(pendingFile.file.size / 1024 / 1024).toFixed(2)} MB</span>
                    <Button
                      icon="pi pi-times"
                      className="p-button-text p-button-rounded p-button-danger"
                      onClick={() => {
                        if (pendingFile.preview) URL.revokeObjectURL(pendingFile.preview);
                        setEditPendingFiles(prev => prev.filter(f => f.id !== pendingFile.id));
                      }}
                      style={{ width: '24px', height: '24px' }}
                      disabled={savingEdit}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-content-between">
              <Button
                icon="pi pi-paperclip"
                onClick={() => editFileInputRef.current?.click()}
                disabled={savingEdit || editPendingFiles.length + editExistingAttachments.length >= MAX_FILES}
                outlined
                size="small"
                label="Добавить файлы"
              />
              
              <div className="flex gap-1">
                <Button
                  label="Отмена"
                  icon="pi pi-times"
                  className="p-button-text p-button-sm"
                  onClick={handleCancelEdit}
                  disabled={savingEdit}
                />
                <Button
                  label="Сохранить"
                  icon="pi pi-save"
                  className="p-button-sm"
                  onClick={() => handleSaveEditComment(comment.id)}
                  loading={savingEdit}
                  disabled={savingEdit || (!editCommentText.trim() && editPendingFiles.length === 0 && editExistingAttachments.length === 0)}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-content-between align-items-start">
            <div className="flex align-items-center gap-2 text-600">
              <span style={{ fontWeight: 'bold', color: '#3b82f6' }}>{comment.userName}</span>
              <i 
                className={getDeviceIcon(comment.device)} 
                style={{ 
                  fontSize: '12px',
                  color: '#afafaf'
                }}
              />
            </div>
            
            <div className="flex gap-1">
              <Button
                icon="pi pi-pencil"
                className="p-button-text p-button-rounded p-button-sm"
                onClick={() => handleEditComment(comment)}
                disabled={editingCommentId !== null}
                tooltip="Редактировать комментарий"
                tooltipOptions={{ position: 'top' }}
                style={{ 
                  width: '28px', 
                  height: '28px',
                  color: '#3b82f6'
                }}
              />
              
              <Button
                icon="pi pi-trash"
                className="p-button-text p-button-rounded p-button-sm p-button-danger"
                onClick={() => handleDeleteComment(comment.id, comment.userName)}
                loading={deletingCommentId === comment.id}
                disabled={deletingCommentId !== null || editingCommentId !== null}
                tooltip="Удалить комментарий"
                tooltipOptions={{ position: 'top' }}
                style={{ 
                  width: '28px', 
                  height: '28px',
                  color: '#ef4444'
                }}
              />
            </div>
          </div>
          
          <div className="text-sm mb-2">
            <span style={{ color: '#afafaf' }}>{formatDate(comment.createdAt)}</span>
          </div>
          
          <div className="text-base line-height-3" style={{ 
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}>
            {comment.text}
          </div>
          
          {comment.attachments && comment.attachments.length > 0 && (
            <div className="flex align-items-center gap-2 flex-wrap mt-2">
              {comment.attachments.map((attachment) => {
                const preview = getAttachmentPreview(attachment);
                const isImage = attachment.name?.match(/\.(png|jpg|jpeg|gif|webp)$/i);
                
                return (
                  <div 
                    key={attachment.id} 
                    className="flex align-items-center gap-2 bg-gray-100 p-2 border-round"
                    style={{ borderRadius: '8px' }}
                  >
                    <div
                      className="flex align-items-center gap-2 cursor-pointer"
                      data-pr-tooltip="Дважды щелкните, чтобы открыть или скачать файл"
                      data-pr-position="top"
                      onDoubleClick={() => handleAttachmentDoubleClick(attachment)}
                    >
                      {preview && isImage ? (
                        <img 
                          src={preview} 
                          alt="preview" 
                          style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }} 
                        />
                      ) : (
                        <i className="pi pi-file" style={{ fontSize: '24px', color: '#6c757d' }} />
                      )}
                      <span className="text-sm" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {attachment.name}
                      </span>
                    </div>
                    <Button
                      type="button"
                      icon="pi pi-download"
                      className="p-button-text p-button-rounded p-button-sm"
                      onClick={() => handleAttachmentDownload(attachment)}
                      style={{ width: '28px', height: '28px' }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};